import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BodyText, Icon, Link, List, ModalDialog, Span } from '@gaiads/telia-react-component-library';
import { useDispatch } from 'react-redux';
import { AdminCategory } from '@cdaas/api-clients/dist/product-service-openapi';

import CategoryItem from '../CategoryItem/CategoryItem';
import { addOneAdminCategory, removeOneAdminCategory, updateOneAdminCategory } from '../../../../store/adminCategories';
import { setNotification } from '../../../../store/ui';
import { AdminSettingsErrorProps } from '../../AdminSettings';
import { productServiceAdminCategoriesClient } from '../../../../api-client/product-service-client';

interface CategoriesListProps extends AdminSettingsErrorProps {
  categories: AdminCategory[];
  currentCategoryId?: AdminCategory['id'];
  newCategory?: AdminCategory;
  handleCategoryClick: (category: AdminCategory) => void;
  handleCategoryAddition: () => void;
  handleNewCategoryBlur: () => void;
}

export default function CategoriesList(props: CategoriesListProps) {
  const {
    categories,
    currentCategoryId,
    errors,
    setErrors,
    handleCategoryClick,
    handleCategoryAddition,
    handleNewCategoryBlur,

    newCategory,
  } = props;

  const { t } = useTranslation('adminSettings');

  const dispatch = useDispatch();

  const [categoryIdToDelete, setCategoryIdToDelete] = useState<AdminCategory['id']>();

  const handleCategoryDelete = async (id: AdminCategory['id']) => {
    try {
      await productServiceAdminCategoriesClient.deleteAdminCategory(id);
      dispatch(removeOneAdminCategory(id));
    } catch {
      dispatch(
        setNotification({
          text: t('productCategories.errors.deleteCategory'),
          show: true,
          type: 'warning',
        }),
      );
    } finally {
      setCategoryIdToDelete(undefined);
    }
  };

  const handleCategoryCreate = async (category: AdminCategory): Promise<boolean> => {
    try {
      const response = await productServiceAdminCategoriesClient.postAdminCategory(category);
      dispatch(addOneAdminCategory(response.data));
      handleNewCategoryBlur();
    } catch {
      dispatch(
        setNotification({
          text: t('productCategories.errors.createCategory'),
          show: true,
          type: 'warning',
        }),
      );
    }
    return false;
  };
  const handleCategoryUpdate = async (category: AdminCategory): Promise<boolean> => {
    try {
      const response = await productServiceAdminCategoriesClient.putAdminCategory(category.id, category);
      dispatch(updateOneAdminCategory(response.data));
      return true;
    } catch {
      dispatch(
        setNotification({
          text: t('productCategories.errors.updateCategory'),
          show: true,
          type: 'warning',
        }),
      );
      return false;
    }
  };
  const handleDelete = (category: AdminCategory) => {
    setCategoryIdToDelete(category.id);
  };

  return (
    <>
      <List className="product-categories__category-list" flexItem>
        {categories.map(category => (
          <CategoryItem
            key={`category-${category.id}`}
            category={category}
            currentCategoryId={currentCategoryId}
            errors={errors}
            setErrors={setErrors}
            handleClick={handleCategoryClick}
            handleCategoryUpdate={handleCategoryUpdate}
            handleDelete={handleDelete}
          />
        ))}
        {newCategory && (
          <CategoryItem
            category={newCategory}
            currentCategoryId={currentCategoryId}
            errors={errors}
            setErrors={setErrors}
            isNew={true}
            handleCategoryCreate={handleCategoryCreate}
            handleDelete={handleDelete}
            handleBlur={handleNewCategoryBlur}
          />
        )}
        <List.Item className="product-categories__category-item product-categories__add">
          <Span onClick={handleCategoryAddition}>
            <Link>
              <Icon name="plus" size="sm" className="product-categories__add-icon" />
              {t('productCategories.links.addCategory')}
            </Link>
          </Span>
        </List.Item>
      </List>
      <ModalDialog
        title={t('productCategories.deleteCategoryConfirmationDialog.title')}
        closeButtonText={t('productCategories.deleteCategoryConfirmationDialog.buttons.cancel')}
        acceptButtonText={t('productCategories.deleteCategoryConfirmationDialog.buttons.delete')}
        isOpen={!!categoryIdToDelete}
        onCloseClick={() => setCategoryIdToDelete(undefined)}
        onAcceptClick={() => categoryIdToDelete && handleCategoryDelete(categoryIdToDelete)}
      >
        <BodyText>{t('productCategories.deleteCategoryConfirmationDialog.content')}</BodyText>
      </ModalDialog>
    </>
  );
}
